import { useLayoutEffect, useRef } from 'react';
import './bracket-diagram.styles.scss';
import { Link } from 'react-router-dom';

// A bracket drawn the traditional way: one column per round, lines joining
// each pair of games to the next. Wider than a phone, so it scrolls
// sideways, opening on the round being played.
// bracket: from summary/postseason (or emptyNationalBracket). rounds: which
// round indexes to draw; filter(matchup): e.g. one region. emptyLabel(m,
// side): text for a slot with no team yet. winnerLabel: adds a last column
// for the winner of the final game drawn (e.g. 'Champion').
const BracketDiagram = ({ bracket, teams, teamLink, gameLink, highlightId, rounds, filter, emptyLabel, winnerLabel }) => {

    const roundList = rounds ?? bracket.rounds.map((_, i) => i);
    const columns = roundList.map(r => ({
        r,
        name: bracket.rounds[r].name,
        matchups: bracket.rounds[r].matchups.filter(m => !filter || filter(m)),
    }));
    const last = columns.at(-1)?.matchups;
    const winner = winnerLabel && last?.length === 1 ? last[0].winner : undefined;
    const current = bracket.empty ? 0 :
        Math.max(0, columns.findIndex(c => c.matchups.some(m => !m.winner)));

    const scroller = useRef(null);
    const cols = useRef([]);
    useLayoutEffect(() => {
        const box = scroller.current;
        const col = cols.current[current];
        if (box && col) box.scrollLeft = col.offsetLeft;
    }, [current]);

    const entryRow = (m, side) => {
        const entry = m[side];
        const team = entry?.team ? teams?.[entry.team] : null;
        const game = m.games?.[0];
        const score = game ? (game.home === entry?.team ? game.result.home : game.result.away) : null;
        const cls = [
            'bd-team',
            m.winner && entry?.team === m.winner ? 'won' : '',
            m.winner && entry?.team && entry.team !== m.winner ? 'lost' : '',
            entry?.team && entry.team === highlightId ? 'mine' : '',
        ].join(' ');
        return (
            <div className={cls}>
                <span className='bd-seed'>{entry?.seed ?? ''}</span>
                {team ?
                    <Link to={teamLink(team.id)} className='bd-name'>{team.name}</Link> :
                    <span className='bd-name bd-empty'>{emptyLabel?.(m, side) ?? ''}</span>
                }
                {score != null && <Link to={gameLink(game.id)} className='bd-score' aria-label='Box score'>{score}</Link>}
            </div>
        );
    };

    return (
        <div className='bracket-diagram' ref={scroller}>
            <div className='bd-rounds'>
                {columns.map((c, ci) => (
                    <div key={c.r} className='bd-col' ref={el => { cols.current[ci] = el; }}>
                        <div className='bd-head'>{c.name}</div>
                        <div className='bd-body'>
                            {c.matchups.map((m, i) => (
                                <div key={m.index} className={[
                                    'bd-slot',
                                    ci > 0 ? 'in' : '',
                                    ci < columns.length - 1 || winnerLabel ? 'out' : '',
                                    i % 2 === 0 ? 'upper' : 'lower',
                                    c.matchups.length === 1 ? 'only' : '',
                                ].join(' ')}>
                                    <div className={`bd-match ${m.pending ? 'next' : ''}`}>
                                        {entryRow(m, 'top')}
                                        {entryRow(m, 'bottom')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                {winnerLabel &&
                    <div className='bd-col bd-winner'>
                        <div className='bd-head'>{winnerLabel}</div>
                        <div className='bd-body'>
                            <div className='bd-slot in only'>
                                <div className='bd-match'>
                                    {winner && teams?.[winner] ?
                                        <div className={`bd-team won ${winner === highlightId ? 'mine' : ''}`}>
                                            <span className='bd-seed'>{seedOf(last[0], winner)}</span>
                                            <Link to={teamLink(winner)} className='bd-name'>{teams[winner].name}</Link>
                                        </div> :
                                        <div className='bd-team'><span className='bd-seed' /><span className='bd-name bd-empty'>To be decided</span></div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

function seedOf(m, teamId) {
    return m.top?.team === teamId ? m.top.seed : m.bottom?.seed;
}

export default BracketDiagram
